import {
  Box,
  Button,
  Checkbox,
  Flex,
  Heading,
  Icon,
  List,
  ListIcon,
  ListItem,
  Spinner,
  Table,
  Tag,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useDisclosure,
  useToast,
} from '@chakra-ui/react'
import { useContext, useEffect, useState } from 'react'
import CurrencyFormat from 'react-currency-format'
import {
  RiAddLine,
  RiCheckLine,
  RiDeleteBin6Line,
  RiPencilLine,
  RiPrinterLine,
  RiSettings3Line,
} from 'react-icons/ri'
import { useMutation } from 'react-query'
import { Link } from 'react-router-dom'
import { Modal } from '../../components/Modal'
import { Pagination } from '../../components/Pagination'
import type { LabelSize } from '../../utils/labelSize'
import { ImprimirEtiquetaModal } from './ImprimirEtiquetaModal'
import { MateriaPrimaLabelModal } from './MateriaPrimaLabelModal'
import { SearchContext } from '../../context/SearchContext'
import { RawMaterial } from '../../Interfaces/rawMaterial'
import { api } from '../../services/api'
import { useRawMaterials } from '../../services/hooks/useRawMaterials'

export function RawMaterials() {
  const { searchValue } = useContext(SearchContext)
  const [page, setPage] = useState(1)
  const [statusFilter, setStatusFilter] = useState<boolean | undefined>(undefined)

  const [totalPrice, setTotalPrice] = useState<number>()

  const toast = useToast()

  const { queryContextParams, clearQuery } = useContext(SearchContext)

  // Query para dados paginados
  const { data, isLoading, isFetching, error, refetch } = useRawMaterials({
    page,
    ...queryContextParams,
    is_enable: statusFilter,
  })

  const { isOpen, onOpen, onClose } = useDisclosure()
  const { isOpen: isLabelModalOpen, onOpen: onLabelModalOpen, onClose: onLabelModalClose } =
    useDisclosure()
  const {
    isOpen: isPrintLabelOpen,
    onOpen: onPrintLabelOpen,
    onClose: onPrintLabelClose,
  } = useDisclosure()

  const [currentRawMaterial, setcurrentRawMaterial] = useState<RawMaterial>()
  const [labelSize, setLabelSize] = useState<LabelSize | null>(null)

  useEffect(() => {
    refetch()
  }, [queryContextParams])

  useEffect(() => {
    setPage(1)
    refetch()
  }, [statusFilter])

  useEffect(() => {
    ;(async () => {
      const { data } = await api.get('raw-materials/totalPrice')

      const total = parseFloat(data.totalPrice)

      if (!isNaN(total)) {
        setTotalPrice(total)
      }
    })()
  }, [isOpen])

  useEffect(() => {
    return () => {
      clearQuery()

      refetch()
    }
  }, [])

  const handleRemoveButton = () => {
    if (currentRawMaterial) removeRawMaterial.mutateAsync(currentRawMaterial)

    onClose()
  }

  const removeRawMaterial = useMutation(async (raw_materials: RawMaterial) => {
    const response = await api.delete(`raw-materials/${raw_materials.id}`)

    if (response) {
      toast({ description: 'Removido com sucesso', status: 'success' })
      refetch()
    }

    onClose()
    return response.data
  })

  const handleModalOpen = (raw_material) => {
    setcurrentRawMaterial(raw_material)

    onOpen()
  }

  // Removido filtro local - o filtro deve ser aplicado no servidor via queryContextParams

  return (
    <Box flex="1" borderRadius="8" bg="gray.800" p="8">
      <Flex mb="8" justifyContent="space-between" align="center">
        <Box>
          <Heading size="lg" fontWeight="normal">
            Matéria Prima - R${' '}
            <CurrencyFormat
              displayType="text"
              decimalSeparator="."
              thousandSeparator=","
              value={totalPrice?.toFixed(2)}
            />
            {!isLoading && isFetching && <Spinner size="sm" color="gray.500" ml="4" />}
          </Heading>
          {data && (
            <Flex mt="2" gap="4" fontSize="sm" color="gray.400">
              <Text>
                Total: <strong>{data.totalCount}</strong> materiais
              </Text>
              <Text color="green.400">
                Habilitados: <strong>{data.raw_materials.filter((m) => m.is_enable).length}</strong>
              </Text>
              <Text color="red.400">
                Desabilitados: <strong>{data.raw_materials.filter((m) => !m.is_enable).length}</strong>
              </Text>
            </Flex>
          )}
        </Box>

        <Flex gap="2">
          <Button
            size="sm"
            fontSize="sm"
            colorScheme="blue"
            variant="outline"
            leftIcon={<Icon as={RiPrinterLine} fontSize="18" />}
            onClick={onPrintLabelOpen}>
            Imprimir Etiqueta
          </Button>
          <Button
            size="sm"
            fontSize="sm"
            colorScheme="gray"
            variant="outline"
            leftIcon={<Icon as={RiSettings3Line} fontSize="18" />}
            onClick={onLabelModalOpen}>
            Configurar Etiqueta
          </Button>
          <Button
            as={Link}
            to="/raw-materials/create"
            size="sm"
            fontSize="sm"
            colorScheme="pink"
            leftIcon={<Icon as={RiAddLine} fontSize="20" />}>
            Criar Novo
          </Button>
        </Flex>
      </Flex>

      <MateriaPrimaLabelModal
        open={isLabelModalOpen}
        onClose={onLabelModalClose}
        onApply={(size) => setLabelSize(size)}
      />

      <ImprimirEtiquetaModal open={isPrintLabelOpen} onClose={onPrintLabelClose} />

      {/* Filtros de Status */}
      <Flex mb="6" gap="4" align="center">
        <Text fontSize="sm" fontWeight="medium" color="gray.300">
          Filtrar por status:
        </Text>
        <Button
          size="sm"
          variant={statusFilter === undefined ? 'solid' : 'outline'}
          colorScheme="blue"
          onClick={() => setStatusFilter(undefined)}>
          Todos
        </Button>
        <Button
          size="sm"
          variant={statusFilter === true ? 'solid' : 'outline'}
          colorScheme="green"
          onClick={() => setStatusFilter(true)}>
          Habilitados
        </Button>
        <Button
          size="sm"
          variant={statusFilter === false ? 'solid' : 'outline'}
          colorScheme="red"
          onClick={() => setStatusFilter(false)}>
          Desabilitados
        </Button>
      </Flex>

      {isLoading ? (
        <Flex justify="center">
          <Spinner />
        </Flex>
      ) : error ? (
        <Flex justify="center">
          <Text>Falha ao obter os matérias primas</Text>
        </Flex>
      ) : (
        <Table colorScheme="whiteAlpha">
          <Thead>
            <Tr>
              <Th px={['4', '6', '6']} color="gray.300" width="8">
                <Checkbox colorScheme="pink" />
              </Th>
              <Th>Produto</Th>
              <Th>Quantidade</Th>
              <Th>Categoria</Th>
              <Th>Preço</Th>
              <Th width="8" />
              <Th width="8" />
            </Tr>
          </Thead>
          <Tbody>
            {data?.raw_materials.map((material: RawMaterial) => {
              return (
                <Tr
                  key={material.id}
                  opacity={material.is_enable ? 1 : 0.6}
                  bg={material.is_enable ? 'transparent' : 'gray.700'}>
                  <Td px={['4', '4', '6']}>
                    <Checkbox colorScheme="pink" />
                  </Td>
                  <Td>
                    <Box>
                      <Text fontWeight="bold">{material.name}</Text>
                      <Text fontSize="small" color="gray.300">
                        {material.provider.name}
                      </Text>
                    </Box>
                  </Td>

                  <Td>
                    <Tag>
                      {material.amount} {material.measurement_unit}
                    </Tag>
                  </Td>
                  <Td>
                    {(() => {
                      switch (material.category) {
                        case 'TINTASCOLORIDAS':
                          return <span>Tintas Coloridas</span>
                        case 'MANUTENCAO':
                          return <span>Manutenção</span>
                        case 'LIMPEZA':
                          return <span>Limpeza</span>
                        case 'EPI':
                          return <span>Epis</span>
                        case 'PRODUCAO':
                          return <span>Produção</span>
                      }
                    })()}
                  </Td>
                  <Td>
                    <CurrencyFormat
                      displayType="text"
                      decimalSeparator=","
                      thousandSeparator="."
                      prefix="R$ "
                      value={material.price}
                    />
                  </Td>
                  <Td>
                    <Button
                      as={Link}
                      to={`/raw-materials/edit/${material.id}`}
                      size="sm"
                      fontSize="sm"
                      colorScheme="purple"
                      leftIcon={<Icon as={RiPencilLine} />}>
                      Editar
                    </Button>
                  </Td>
                  <Td>
                    <Button
                      onClick={() => handleModalOpen(material)}
                      size="sm"
                      fontSize="sm"
                      colorScheme="red"
                      leftIcon={<Icon as={RiDeleteBin6Line} />}>
                      Remover
                    </Button>
                  </Td>
                </Tr>
              )
            })}
          </Tbody>
        </Table>
      )}

      <Pagination totalCountOfRegisters={data?.totalCount} currentPage={page} onPageChange={setPage} />

      <Modal
        isOpen={isOpen}
        onClose={onClose}
        title="Remover Produto"
        buttonTitle="Remover"
        buttonAction={handleRemoveButton}>
        <List spacing={3}>
          <ListItem>
            <ListIcon as={RiCheckLine} color="green.500" />
            {`Nome: ${currentRawMaterial?.name}`}
          </ListItem>
          <ListItem>
            <ListIcon as={RiCheckLine} color="green.500" />
            {`Preço: R$ ${currentRawMaterial?.price}`}
          </ListItem>
          <ListItem>
            <ListIcon as={RiCheckLine} color="green.500" />
            {`Quantidade: ${currentRawMaterial?.amount}`}
          </ListItem>
          <ListItem>
            <ListIcon as={RiCheckLine} color="green.500" />
            {`Categoria: ${currentRawMaterial?.category}`}
          </ListItem>
        </List>
      </Modal>
    </Box>
  )
}
